/**
 * Pure helpers for the DVIR template builder.
 *
 * This module is the single place the DVIR template API contract is encoded
 * (truck-backend/docs/api/DVIR_Template_Builder_API_Contract.md).
 *
 * Keep it import-free. Anything that reaches src/util/util-formaters -- which
 * includes Field, FieldsManager and util-helpers -- crashes vitest at
 * util-constants:1100.
 */

export const DVIR_STATUS = {DRAFT: 'draft', ACTIVE: 'active', PAUSED: 'paused'};
export const DVIR_STATUSES = [DVIR_STATUS.DRAFT, DVIR_STATUS.ACTIVE, DVIR_STATUS.PAUSED];

export const SCOPE_MODE = {ALL: 'all', SELECTED: 'selected'};
export const SCOPE_ENTITIES = ['drivers', 'trucks', 'trailers', 'equipment_types'];

export const CADENCES = [
    'every_pre_trip',
    'every_post_trip',
    'every_takeover_swap',
    'one_time_only',
    'weekly',
    'monthly',
    'quarterly',
    'annually',
    'by_mileage',
    'by_engine_hours'
];

export const RESPONSE_TYPES = ['select_answer', 'photo_only', 'note_only'];
export const CATEGORIES = ['driver', 'truck', 'trailer'];

/**
 * Tailwind classes for the submission result badge. Shared by the submissions
 * list and the submission detail card -- keep it in one place so a color
 * change cannot make the two disagree.
 */
export const RESULT_BADGE_CLASS = {
    passed: "bg-green-100 text-green-800",
    defects: "bg-red-100 text-red-800",
    na: "bg-tm-gray-100 text-tm-gray-700"
};

function toIntOrNull(value) {
    if (value === null || value === undefined || value === '') return null;

    const parsed = parseInt(value, 10);

    return Number.isNaN(parsed) ? null : parsed;
}

function toNullable(value) {
    return value === '' || value === undefined ? null : value;
}

// Client-only identity, never sent to the API. React needs a stable key for
// list rows so removing/reordering an item moves the right DOM node instead
// of reusing one by array slot -- the server `id` can't serve that role
// because every unsaved step/option has id: null (collides across multiple
// unsaved rows). This matters even more for answer options than steps: each
// option row holds a text input, so an index-keyed row would make a removal
// move the user's caret into a different row's input mid-edit.
// nextStepUid() and nextOptionUid() share one counter so a step's uid and an
// option's uid can never collide. templateToApiPayload (below) builds each
// question/option object explicitly, field by field, rather than spreading --
// so this can never leak into the PATCH payload. A plain incrementing counter
// keeps this module import-free (no crypto/uuid import) and deterministic
// within a session.
let nextUidNumber = 1;

function nextUid(prefix) {
    return prefix + '-' + (nextUidNumber++);
}

function nextStepUid() {
    return nextUid('step');
}

function nextOptionUid() {
    return nextUid('option');
}

export function emptyScopeEntity() {
    return {mode: SCOPE_MODE.ALL, selected: []};
}

export function emptyAnswerOption(label = '') {
    return {
        id: null,
        _uid: nextOptionUid(),
        label: label,
        require_note: false,
        warn_safety: false,
        warn_dispatch: false,
        create_defect: false,
        block_dispatch: false
    };
}

export function emptyStep() {
    return {
        id: null,
        _uid: nextStepUid(),
        question_name: '',
        instruction: '',
        response_type: ['select_answer'],
        photo_requirement: true,
        note_requirement: false,
        category: [],
        answer_options: [emptyAnswerOption(), emptyAnswerOption()]
    };
}

export function emptyTemplate() {
    return {
        id: null,
        template_name: '',
        description: '',
        status: DVIR_STATUS.DRAFT,
        office_id: '',
        office_name: '',
        contact_group_id: '',
        contact_group_name: '',
        effective_start: '',
        effective_end: '',
        assignment_scope: SCOPE_ENTITIES.reduce((memo, entity) => {
            memo[entity] = emptyScopeEntity();
            return memo;
        }, {}),
        when_to_require: [],
        mileage_interval: '',
        mileage_start: '',
        hours_interval: '',
        hours_start: '',
        completion_tracked_by: null,
        steps: []
    };
}

export function templateFromApi(data) {
    const source = data ?? {};
    const template = emptyTemplate();

    template.id = source.id ?? null;
    template.template_name = source.template_name ?? '';
    template.description = source.description ?? '';
    template.status = DVIR_STATUSES.includes(source.status) ? source.status : DVIR_STATUS.DRAFT;
    template.office_id = source.office_id ?? '';
    template.office_name = source.office_name ?? '';
    template.contact_group_id = source.contact_group_id ?? '';
    template.contact_group_name = source.contact_group_name ?? '';
    template.effective_start = source.effective_start ?? '';
    template.effective_end = source.effective_end ?? '';
    template.completion_tracked_by = source.completion_tracked_by ?? null;
    template.when_to_require = (source.when_to_require ?? []).filter(it => CADENCES.includes(it));
    template.mileage_interval = source.mileage_interval ?? '';
    template.mileage_start = source.mileage_start ?? '';
    template.hours_interval = source.hours_interval ?? '';
    template.hours_start = source.hours_start ?? '';

    template.assignment_scope = SCOPE_ENTITIES.reduce((memo, entity) => {
        const scope = (source.assignment_scope ?? {})[entity] ?? {};

        memo[entity] = {
            mode: scope.mode === SCOPE_MODE.SELECTED ? SCOPE_MODE.SELECTED : SCOPE_MODE.ALL,
            selected: (scope.selected ?? []).map(it => ({
                id: Number(it.id),
                name: it.name ?? '',
                secondary: it.secondary ?? null
            }))
        };

        return memo;
    }, {});

    template.steps = (source.questions ?? [])
        .slice()
        .sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0))
        .map(question => ({
            id: question.id ?? null,
            _uid: nextStepUid(),
            question_name: question.question_name ?? '',
            instruction: question.instruction ?? '',
            response_type: (question.response_type ?? []).filter(it => RESPONSE_TYPES.includes(it)),
            photo_requirement: !!question.photo_requirement,
            note_requirement: !!question.note_requirement,
            category: (question.category ?? []).filter(it => CATEGORIES.includes(it)),
            answer_options: (question.answer_options ?? []).map(option => ({
                id: option.id ?? null,
                _uid: nextOptionUid(),
                label: option.label ?? '',
                require_note: !!option.require_note,
                warn_safety: !!option.warn_safety,
                warn_dispatch: !!option.warn_dispatch,
                create_defect: !!option.create_defect,
                block_dispatch: !!option.block_dispatch
            }))
        }));

    return template;
}

export function templateToApiPayload(state) {
    const template = state ?? emptyTemplate();
    const cadences = template.when_to_require ?? [];
    const hasMileage = cadences.includes('by_mileage');
    const hasHours = cadences.includes('by_engine_hours');

    return {
        id: template.id,
        template_name: template.template_name ?? '',
        description: template.description ?? '',
        status: DVIR_STATUSES.includes(template.status) ? template.status : DVIR_STATUS.DRAFT,
        office_id: toIntOrNull(template.office_id),
        contact_group_id: toIntOrNull(template.contact_group_id),
        effective_start: toNullable(template.effective_start),
        effective_end: toNullable(template.effective_end),
        assignment_scope: SCOPE_ENTITIES.reduce((memo, entity) => {
            const scope = (template.assignment_scope ?? {})[entity] ?? emptyScopeEntity();

            memo[entity] = {
                mode: scope.mode,
                selected_ids: scope.mode === SCOPE_MODE.SELECTED
                    ? (scope.selected ?? []).map(it => Number(it.id))
                    : []
            };

            return memo;
        }, {}),
        when_to_require: cadences,
        mileage_interval: hasMileage ? toIntOrNull(template.mileage_interval) : null,
        mileage_start: hasMileage ? toIntOrNull(template.mileage_start) : null,
        hours_interval: hasHours ? toIntOrNull(template.hours_interval) : null,
        hours_start: hasHours ? toIntOrNull(template.hours_start) : null,
        completion_tracked_by: template.completion_tracked_by ?? null,
        questions: (template.steps ?? []).map((step, index) => {
            const types = step.response_type ?? [];

            return {
                step_order: index + 1,
                question_name: step.question_name ?? '',
                instruction: step.instruction ?? '',
                response_type: types,
                photo_requirement: types.includes('photo_only') ? !!step.photo_requirement : false,
                note_requirement: types.includes('note_only') ? !!step.note_requirement : false,
                category: step.category ?? [],
                answer_options: (step.answer_options ?? []).map(option => ({
                    label: option.label ?? '',
                    require_note: !!option.require_note,
                    warn_safety: !!option.warn_safety,
                    warn_dispatch: !!option.warn_dispatch,
                    create_defect: !!option.create_defect,
                    block_dispatch: !!option.block_dispatch
                }))
            };
        })
    };
}

function isPositiveInt(value) {
    const parsed = toIntOrNull(value);

    return parsed !== null && parsed > 0;
}

function isBlank(value) {
    return !String(value ?? '').trim();
}

/**
 * The 9 publish rules from the API contract §5.2. Runs only when the resulting
 * status is active or paused -- a draft target never triggers them.
 *
 * Returns [] when publishable. Field paths match the server's dotted convention
 * so mapApiErrorsToFields consumes client and server errors identically.
 */
export function validateForPublish(state, translate) {
    const template = state ?? emptyTemplate();
    const errors = [];

    if (isBlank(template.template_name)) {
        errors.push({field: 'template_name', message: translate('message.dvir_template_name_required')});
    }

    if (isBlank(template.effective_start)) {
        errors.push({field: 'effective_start', message: translate('message.dvir_effective_start_required')});
    }

    const cadences = template.when_to_require ?? [];

    if (!cadences.length) {
        errors.push({field: 'when_to_require', message: translate('message.dvir_cadence_required')});
    }

    if (cadences.includes('by_mileage') && !isPositiveInt(template.mileage_interval)) {
        errors.push({field: 'mileage_interval', message: translate('message.dvir_mileage_interval_required')});
    }

    if (cadences.includes('by_engine_hours') && !isPositiveInt(template.hours_interval)) {
        errors.push({field: 'hours_interval', message: translate('message.dvir_hours_interval_required')});
    }

    const steps = template.steps ?? [];

    if (!steps.length) {
        errors.push({field: 'questions', message: translate('message.dvir_step_required')});
    }

    steps.forEach((step, index) => {
        if (isBlank(step.question_name)) {
            errors.push({
                field: `questions.${index}.question_name`,
                message: translate('message.dvir_question_name_required')
            });
        }

        const types = step.response_type ?? [];

        if (!types.length) {
            errors.push({
                field: `questions.${index}.response_type`,
                message: translate('message.dvir_response_type_required')
            });
        }

        const namedOptions = (step.answer_options ?? []).filter(it => !isBlank(it.label));

        if (types.includes('select_answer') && namedOptions.length < 2) {
            errors.push({
                field: `questions.${index}.answer_options`,
                message: translate('message.dvir_two_answer_options_required')
            });
        }
    });

    return errors;
}

/**
 * Splits a flat [{field, message}] list -- from validateForPublish or from a
 * server VALIDATION response -- into header errors and per-step-index errors.
 */
export function mapApiErrorsToFields(errors) {
    return (errors ?? []).reduce((memo, error) => {
        const field = error?.field;

        if (!field) return memo;

        const stepMatch = /^questions\.(\d+)\.(.+)$/.exec(field);

        if (stepMatch) {
            const index = Number(stepMatch[1]);

            memo.steps[index] = Object.assign({}, memo.steps[index], {[stepMatch[2]]: error.message});
        } else {
            memo.header[field] = error.message;
        }

        return memo;
    }, {header: {}, steps: {}});
}
